import { Component, ElementRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { OrderService } from "@gloria-public/order/domain/services/order.service";
import { TranslateService } from "@ngx-translate/core";
import { ApiService } from "@shared/services/api.service";
import { AuthService } from "@shared/services/auth.service";
import { ConnectionEventsService } from "@shared/services/connection-events.service";
import { LanguageService } from "@shared/services/language.service";
import { SessionService } from "@shared/services/session.service";
import { UserService } from "@shared/services/user.service";
import { Subject, distinctUntilChanged, first, startWith, takeUntil } from "rxjs";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
})
export class HeaderComponent implements OnInit {
  @ViewChild("languageSelector") languageSelector!: ElementRef;

  public cartItemsCount$ = this.orderService.cartItemsCount$;
  public isMenuOpen = false;
  public selectedLanguage!: string;
  public languages: string[] = [];
  public isLanguageDropdownOpen = false;

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  /**
   * Closes the mobile menu or language dropdown when clicking outside.
   * @param event The document click event.
   */
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Close mobile menu when clicking outside the header
    if (!this.elementRef.nativeElement.contains(target)) {
      this.isMenuOpen = false;
    }

    // Close language dropdown when clicking outside it
    if (this.isLanguageDropdownOpen && this.languageSelector && !this.languageSelector.nativeElement.contains(target)) {
      this.isLanguageDropdownOpen = false;
    }
  }

  constructor(
    private authService: AuthService,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private orderService: OrderService,
    private elementRef: ElementRef,
    private snackBar: MatSnackBar,
    public sessionService: SessionService,
    private userService: UserService,
    private connectionEventsService: ConnectionEventsService
  ) {}

  /**
   * Initializes languages and updates the cart on language changes.
   */
  ngOnInit(): void {
    this.languageService.getLanguages().subscribe((res) => {
      this.languages = res.map((c) => c.code);
    });

    this.translateService.onLangChange
      .pipe(
        startWith({
          lang: this.translateService.currentLang,
        }),
        distinctUntilChanged((a, b) => a.lang === b.lang),
        takeUntil(this.onDestroy$)
      )
      .subscribe((event) => {
        this.orderService.initializeCart(event.lang);
      });
  }

  /**
   * Gets current application language
   * @return Current language code
   */
  public get currentLang(): string {
    return this.translateService.currentLang;
  }

  /**
   * Sets application language and persists it in session
   * @param value Language code to set
   */
  public set currentLang(value: string) {
    this.translateService.use(value);
    this.sessionService.userLang = value;
  }

  /**
   * Selects a language from dropdown
   * @param lang Language code
   * @param event Click event
   */
  public selectLanguage(lang: string, event: Event): void {
    event.stopPropagation();

    // No need to do anything if the language hasn't changed
    if (lang === this.currentLang) {
      this.isLanguageDropdownOpen = false;
      return;
    }

    if (this.sessionService.isLoggedIn || this.sessionService.isGuest) {
      this.userService.changeLanguage(lang).subscribe({
        next: () => {
          this.currentLang = lang;
          this.isLanguageDropdownOpen = false;
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
        },
      });
    } else {
      this.currentLang = lang;
      this.isLanguageDropdownOpen = false;
    }
  }

  /**
   * Toggles the language selector dropdown.
   * @param event The click event.
   */
  public toggleLanguageDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;
  }

  /**
   * Logs out the current user and clears session
   */
  public logout(): void {
    if (this.sessionService.isLoggedIn) {
      this.authService
        .logout()
        .pipe(first())
        .subscribe({
          next: () => {
            this.connectionEventsService.notifyLogout();
          },
          error: () => {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
            this.connectionEventsService.notifyLogout();
          },
        });
    }
  }

  /**
   * Toggles the mobile navigation menu.
   * @param event The click event.
   */
  public toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isMenuOpen = !this.isMenuOpen;
  }
}
